import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

export default function JobConfirm() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [rating, setRating] = useState(0);
  const [issue, setIssue] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => { api.get(`/jobs/${id}/summary`).then(({ data }) => { setJob(data); setDone(data.customer_confirmed); }).catch(() => setJob(false)); }, [id]);

  const submit = async (withIssue) => {
    try { await api.post(`/jobs/${id}/confirm`, { rating: rating || null, issue: withIssue ? issue : null }); toast.success(withIssue ? "Issue reported" : "Thank you for confirming"); setDone(true); }
    catch (_) { toast.error("Could not submit"); }
  };

  if (job === false) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-gray-400">Job not found.</div>;
  if (!job) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-gray-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-5 py-10" data-testid="job-confirm-page">
      <Seo title="Confirm your ERRĀH service" description="Confirm completion of your ERRĀH service." />
      <div className="w-full max-w-lg">
        <div className="text-center mb-6"><img src="/errah-logo.png" alt="ERRĀH" className="h-16 mx-auto" /></div>
        <div className="hair-border bg-white/[0.02] p-7">
          <p className="errah-label gold-text mb-2">Service completed</p>
          <h1 className="serif-head text-2xl mb-1">{job.service}</h1>
          <p className="text-sm text-gray-400">By {job.applicator_name || "ERRĀH applicator"} {job.applicator_code ? `(${job.applicator_code})` : ""} · {job.completed_at?.slice(0, 10)}</p>

          {job.after_photos?.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-2" data-testid="confirm-photos">
              {job.after_photos.slice(0, 6).map((u, i) => <img key={i} src={u} alt="" className="aspect-square object-cover hair-border" />)}
            </div>
          )}

          {done ? (
            <div className="mt-6 flex items-center gap-2 text-emerald-400" data-testid="confirm-done"><Check size={18} /> Completion confirmed. Thank you!</div>
          ) : (
            <div className="mt-6">
              <p className="errah-label mb-2">Rate your experience</p>
              <div className="flex gap-1 mb-4" data-testid="confirm-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}><Star size={26} className={n <= rating ? "fill-[#C9A45C] text-[#C9A45C]" : "text-gray-600"} /></button>
                ))}
              </div>
              <button onClick={() => submit(false)} data-testid="confirm-btn" className="w-full bg-[#C9A45C] text-black py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors mb-3">Confirm Completion</button>
              <textarea value={issue} onChange={(e) => setIssue(e.target.value)} placeholder="Report an issue (optional)" data-testid="issue-input" className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C] mb-2" rows={2} />
              <button onClick={() => submit(true)} data-testid="report-issue-btn" className="w-full border border-white/20 py-2.5 text-sm hover:bg-white hover:text-black transition-colors">Report an Issue</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
